/**
 * German translations for the end-user verification flow.
 *
 * Keys are the English source strings. Anything absent here falls back to English,
 * so an untranslated string upstream degrades gracefully instead of breaking.
 *
 * Register: informal "du". The flow is used by driving-school applicants, most of
 * them teenagers, and the surrounding customer communication addresses them that way.
 */

export const de: Record<string, string> = {
  // ── Session start ──────────────────────────────────────────────
  'Starting Verification': 'Überprüfung wird gestartet',
  'Initializing your verification session...': 'Deine Sitzung wird vorbereitet …',
  'Missing required parameters': 'Erforderliche Angaben fehlen',
  'Failed to start verification': 'Die Überprüfung konnte nicht gestartet werden',
  'Failed to restart verification': 'Die Überprüfung konnte nicht neu gestartet werden',

  // ── Device choice ──────────────────────────────────────────────
  'How would you like to verify?': 'Wie möchtest du dich ausweisen?',
  'Complete on this device or scan a QR code to use your phone':
    'Direkt auf diesem Gerät oder per QR-Code mit dem Handy',
  'Start Here': 'Hier starten',
  'Use your webcam and upload documents on this device':
    'Kamera dieses Geräts verwenden und Dokumente hier hochladen',

  // ── Document type ──────────────────────────────────────────────
  'Document Type': 'Art des Dokuments',
  'National ID': 'Personalausweis',
  Passport: 'Reisepass',
  "Driver's License": 'Führerschein',
  'Detect automatically': 'Automatisch erkennen',

  // ── Front upload ───────────────────────────────────────────────
  'Upload Your ID': 'Ausweis hochladen',
  'Upload Front of ID': 'Vorderseite hochladen',
  'Upload your government-issued ID to verify your age':
    'Lade deinen amtlichen Lichtbildausweis hoch, um dein Alter nachzuweisen',
  'Take a clear photo of the front of your government-issued ID':
    'Lies die Vorderseite deines amtlichen Lichtbildausweises ein – achte auf ein scharfes, gut lesbares Bild',
  'Take a clear photo of the front of your ID -- no back scan needed':
    'Lies die Vorderseite ein – die Rückseite wird nicht benötigt',
  'JPG, PNG up to 10MB': 'JPG oder PNG, bis 10 MB',

  // ── OCR in progress ────────────────────────────────────────────
  'Reading Your ID': 'Ausweis wird gelesen',
  'Extracting information from the front of your document...':
    'Die Daten der Vorderseite werden ausgelesen …',
  "Please don't close this window": 'Bitte schließe dieses Fenster nicht',
  'Front ID -- Extracted Data': 'Vorderseite – ausgelesene Daten',
  'Date of Birth': 'Geburtsdatum',

  // ── Back upload ────────────────────────────────────────────────
  'Upload Back of ID': 'Rückseite hochladen',
  'We need both sides to cross-validate your identity':
    'Wir brauchen beide Seiten, um die Angaben gegenzuprüfen',
  'Back document uploaded': 'Rückseite hochgeladen',
  'Failed to upload back document': 'Die Rückseite konnte nicht hochgeladen werden',

  // ── Cross-validation ───────────────────────────────────────────
  'Verifying Your Documents': 'Dokumente werden geprüft',
  'Cross-checking the front and back of your ID...':
    'Vorder- und Rückseite werden abgeglichen …',
  'Data cross-validation': 'Datenabgleich',
  'Authenticity check': 'Echtheitsprüfung',
  'Document validation timed out. Please refresh and try again.':
    'Die Prüfung hat zu lange gedauert. Bitte lade die Seite neu und versuche es erneut.',

  // ── Live capture ───────────────────────────────────────────────
  'Processing Verification': 'Überprüfung läuft',
  'Analyzing your live photo...': 'Deine Live-Aufnahme wird ausgewertet …',
  'Live capture verification timed out. Please refresh and try again.':
    'Die Live-Aufnahme hat zu lange gedauert. Bitte lade die Seite neu und versuche es erneut.',
  'Speaker Verification': 'Stimmerkennung',
  'Microphone access denied': 'Zugriff auf das Mikrofon wurde verweigert',
  'Voice verification failed': 'Die Stimmerkennung ist fehlgeschlagen',
  'Failed to get challenge': 'Die Prüfaufgabe konnte nicht geladen werden',

  // ── Generic upload feedback ────────────────────────────────────
  'Document uploaded successfully': 'Dokument erfolgreich hochgeladen',
  'Failed to upload document': 'Das Dokument konnte nicht hochgeladen werden',
  'Verification timed out. Please refresh and try again.':
    'Die Überprüfung hat zu lange gedauert. Bitte lade die Seite neu und versuche es erneut.',

  // ── Result screen ──────────────────────────────────────────────
  Status: 'Status',
  'Age Check': 'Altersprüfung',
  'Minimum Age': 'Mindestalter',
  'Age Verification Failed': 'Altersnachweis fehlgeschlagen',
  'Doc Cross-Check': 'Dokumentenabgleich',
  Verdict: 'Ergebnis',
  'Face Match': 'Gesichtsabgleich',
  Liveness: 'Lebendigkeitsprüfung',
  'AML Screening': 'Sanktionslistenprüfung',
  'Risk Score': 'Risikobewertung',
  Confidence: 'Zuverlässigkeit',
  Rejection: 'Ablehnungsgrund',
  Detail: 'Einzelheiten',
  'Maximum retry attempts reached.': 'Die maximale Anzahl an Versuchen ist erreicht.',
  'Redirecting in 3 seconds...': 'Weiterleitung in 3 Sekunden …',

  // ── Geraeteauswahl (Startbildschirm) ───────────────────────────
  'Loading...': 'Wird geladen …',
  'Preparing your session...': 'Deine Sitzung wird vorbereitet …',
  'Continue on Your Phone': 'Mit dem Handy fortfahren',
  'Better camera quality': 'Bessere Kameraqualität',
  'Guided capture experience': 'Geführte Aufnahme',
  'Higher success rate': 'Höhere Erfolgsquote',
  'Scan QR Code': 'QR-Code scannen',
  'Use This Device': 'Dieses Gerät verwenden',
  'Upload photos and use your webcam to complete verification on this computer.':
    'Bilder hochladen und die Kamera dieses Rechners für die Überprüfung nutzen.',
  'Continue on Desktop': 'Am Rechner fortfahren',
  'Start on This Device': 'Auf diesem Gerät starten',
  Back: 'Zurück',
  'Back to options': 'Zurück zur Auswahl',

  // ── Fehlermeldungen zum Link ───────────────────────────────────
  'This verification link has expired. Please request a new one.':
    'Dieser Link ist abgelaufen. Bitte fordere einen neuen an.',
  'Invalid or expired verification link.': 'Ungültiger oder abgelaufener Link.',
  'Handoff creation failed': 'Der Wechsel aufs Handy ist fehlgeschlagen',
  'Upload failed': 'Der Upload ist fehlgeschlagen',

  // ── Ergebnisanzeige ────────────────────────────────────────────
  Verified: 'Bestätigt',
  'Under Review': 'In Prüfung',
  'Age Verified': 'Alter bestätigt',
  Score: 'Bewertung',
  'Name Match': 'Namensabgleich',
  Address: 'Adresse',

  // ── Adressnachweis ─────────────────────────────────────────────
  'Utility Bill': 'Energierechnung',
  'Bank Statement': 'Kontoauszug',
  'Tax Document': 'Steuerbescheid',

  // ── Lebendigkeitsprüfung: Anweisungen ──────────────────────────
  'Position your face in the oval': 'Bring dein Gesicht in das Oval',
  'Slowly turn your head left': 'Drehe den Kopf langsam nach links',
  'Slowly turn your head right': 'Drehe den Kopf langsam nach rechts',
  'Now look straight ahead': 'Schau jetzt wieder geradeaus',
  'Hold still — capturing...': 'Bitte still halten – Aufnahme läuft …',
  'Liveness check passed!': 'Lebendigkeitsprüfung bestanden!',
  'Liveness check failed. Tap to retry.':
    'Lebendigkeitsprüfung fehlgeschlagen. Zum Wiederholen tippen.',
  'Camera unavailable. Using standard capture.':
    'Kamera nicht verfügbar. Es wird die einfache Aufnahme verwendet.',
  // Hinweis auf das Tonsignal — beim Wegdrehen ist der Bildschirm nicht sichtbar
  'You will hear a signal when it is time to look back at the screen.':
    'Ein Signalton sagt dir, wann du wieder herschauen sollst.',

  // ── Lebendigkeitsprüfung: Kamera ───────────────────────────────
  'Camera access required': 'Kamerazugriff erforderlich',
  'Camera error': 'Kamerafehler',
  'Camera permission denied. Please grant camera access in your browser settings and try again.':
    'Der Kamerazugriff wurde verweigert. Erlaube ihn in den Browser-Einstellungen und versuche es erneut.',
  'No camera detected on this device.': 'Auf diesem Gerät wurde keine Kamera gefunden.',
  'Camera is in use by another app. Close other apps using the camera and try again.':
    'Die Kamera wird von einem anderen Programm verwendet. Schließe es und versuche es erneut.',
  'Camera does not meet requirements (a front-facing camera is needed).':
    'Die Kamera erfüllt die Anforderungen nicht – es wird eine nach vorne gerichtete Kamera benötigt.',
  'Camera access failed for an unknown reason.':
    'Der Kamerazugriff ist aus unbekanntem Grund fehlgeschlagen.',
  'Could not start the video preview. Please try again.':
    'Die Vorschau konnte nicht gestartet werden. Bitte versuche es erneut.',
  'Camera failed to start within 8 seconds. Please try again.':
    'Die Kamera ist nicht innerhalb von 8 Sekunden gestartet. Bitte versuche es erneut.',
  'Camera not supported in this browser': 'Dieser Browser unterstützt keine Kamera',
  'Camera permission denied. Please enable camera access.':
    'Der Kamerazugriff wurde verweigert. Bitte erlaube ihn.',
  'No camera found.': 'Keine Kamera gefunden.',
  'Camera is already in use by another app.':
    'Die Kamera wird bereits von einem anderen Programm verwendet.',

  // ── Lebendigkeitsprüfung: Hinweise und Status ──────────────────
  'Ensure good lighting and face is centred': 'Sorge für gutes Licht und ein mittiges Gesicht',
  'Keep your face visible throughout': 'Dein Gesicht muss durchgehend sichtbar bleiben',
  'Verification complete': 'Überprüfung abgeschlossen',
  'Processing verification...': 'Überprüfung wird verarbeitet …',
  'Analyzing your document and identity': 'Dokument und Identität werden geprüft',
  'Face Detected': 'Gesicht erkannt',
  'Position Face': 'Gesicht positionieren',
  'No face detected. Position your face in the frame.':
    'Kein Gesicht erkannt. Bring dein Gesicht in den Rahmen.',
  'Ensure good lighting and your face is clearly visible.':
    'Sorge für gutes Licht und ein klar sichtbares Gesicht.',
  'Hold your face steady in the frame.': 'Halte dein Gesicht ruhig im Rahmen.',
  'Hold still...': 'Bitte still halten …',
  'Look directly at the camera and blink twice.':
    'Schau direkt in die Kamera und blinzle zweimal.',
  'Follow the on-screen instructions to verify your identity':
    'Folge den Anweisungen am Bildschirm, um deine Identität zu bestätigen',
  'Face lost during countdown. Please try again.':
    'Das Gesicht ging während des Countdowns verloren. Bitte versuche es erneut.',
  'Failed to capture image': 'Die Aufnahme ist fehlgeschlagen',
  'Live capture failed': 'Die Live-Aufnahme ist fehlgeschlagen',
  'Request timed out. Check your connection and try again.':
    'Zeitüberschreitung. Prüfe deine Verbindung und versuche es erneut.',
  'Capture failed. Please try again.': 'Die Aufnahme ist fehlgeschlagen. Bitte versuche es erneut.',
  'Maximum capture attempts reached. Please refresh and try again.':
    'Die maximale Anzahl an Versuchen ist erreicht. Bitte lade die Seite neu.',

  // ── Fortschrittsleiste ─────────────────────────────────────────
  Start: 'Start',
  'Front ID': 'Vorderseite',
  Scanning: 'Auslesen',
  'Back ID': 'Rückseite',
  Checking: 'Prüfung',
  Selfie: 'Aufnahme',
  Done: 'Fertig',
  'Upload ID': 'Ausweis',

  // ── Endergebnis ────────────────────────────────────────────────
  VERIFIED: 'BESTÄTIGT',
  FAILED: 'FEHLGESCHLAGEN',
  REVIEW: 'IN PRÜFUNG',
  'Identity Verified': 'Identität bestätigt',
  'Verification Failed': 'Überprüfung fehlgeschlagen',
  'Try Again': 'Erneut versuchen',
  'Restarting...': 'Wird neu gestartet …',

  // ── Abschlussbildschirm ────────────────────────────────────────
  'Verification Verified': 'Identität bestätigt',
  'Verification Under Review': 'Überprüfung in Bearbeitung',
  'Your identity has been successfully verified.':
    'Deine Identität wurde erfolgreich bestätigt.',
  'Verification could not be completed. Please try again.':
    'Die Überprüfung konnte nicht abgeschlossen werden. Bitte versuche es erneut.',
  'Your verification is being reviewed. You will be notified of the result.':
    'Deine Überprüfung wird geprüft. Du wirst über das Ergebnis informiert.',
  'You can close this window.': 'Du kannst dieses Fenster schließen.',
  PASS: 'BESTANDEN',
  FAIL: 'FEHLGESCHLAGEN',
  WARN: 'HINWEIS',
  ERROR: 'FEHLER',
  CHECKING: 'PRÜFUNG',
  RECOMMENDED: 'EMPFOHLEN',
  Recommended: 'Empfohlen',

  // ── Einstiegsseite ─────────────────────────────────────────────
  'Verify Your Identity': 'Identität bestätigen',
  'Verify Your Age': 'Alter bestätigen',
  'Verification Unavailable': 'Überprüfung nicht verfügbar',
  'Preview Mode': 'Vorschau',
  'Click to upload or drag and drop': 'Zum Hochladen klicken oder Datei hierher ziehen',
  'JPEG, PNG, or PDF (max 10MB)': 'JPEG, PNG oder PDF (max. 10 MB)',
  'Age verification could not be completed.':
    'Der Altersnachweis konnte nicht abgeschlossen werden.',
  'Now upload a proof-of-address document to complete your verification.':
    'Lade nun einen Adressnachweis hoch, um die Überprüfung abzuschließen.',
  "Skip -- I'll do this later": 'Überspringen – später erledigen',
  'Address upload failed:': 'Adressnachweis konnte nicht hochgeladen werden:',
  'Verify Address': 'Adresse prüfen',
  'Processing...': 'Wird verarbeitet …',

  // ── Handy-Übergabe (QR) ────────────────────────────────────────
  'Continue on Phone': 'Mit dem Handy fortfahren',
  'Generate QR Code': 'QR-Code erzeugen',
  'Scan with your phone camera': 'Mit der Handykamera scannen',
  'Better camera quality for liveness and document capture.':
    'Bessere Bildqualität für Aufnahme und Lebendigkeitsprüfung.',
  'Could not generate QR code. Please try again.':
    'Der QR-Code konnte nicht erzeugt werden. Bitte versuche es erneut.',
  'Failed to create handoff session': 'Der Wechsel aufs Handy ist fehlgeschlagen',
  'Completed on mobile device': 'Am Handy abgeschlossen',
  'Pending Review': 'Wird geprüft',
  'Cross-Validation': 'Datenabgleich',

  // ── Handy-Ansicht: Schritte und Aktionen ───────────────────────
  Complete: 'Fertig',
  'Live Photo': 'Live-Aufnahme',
  Voice: 'Stimme',
  'Scan Front of ID': 'Vorderseite einlesen',
  'Scan Back of ID': 'Rückseite einlesen',
  'Scan the front': 'Vorderseite einlesen',
  // Schaltflächen — mit "Jetzt", damit sie sich von der gleichlautenden
  // Überschrift darüber abheben und als Handlungsaufforderung lesen.
  'Take Photo of Front': 'Jetzt Vorderseite einlesen',
  'Take Photo of Back': 'Jetzt Rückseite einlesen',
  'Upload your ID': 'Ausweis hochladen',
  // Live-Aufnahme: hier wird die Person aufgenommen, nicht ein Dokument gelesen
  'Take Selfie': 'Aufnahme machen',
  'Submit Selfie': 'Aufnahme senden',
  'Capture Photo': 'Aufnahme machen',
  'Photo Captured': 'Aufnahme gemacht',
  'Document scanned': 'Dokument eingelesen',
  'Document details confirmed': 'Dokumentdaten bestätigt',
  'Identity document verified': 'Ausweisdokument bestätigt',
  'Face matched successfully': 'Gesichtsabgleich erfolgreich',
  'Secure Session': 'Gesicherte Sitzung',
  'This only takes a moment': 'Das dauert nur einen Augenblick',

  // ── Handy-Ansicht: Kamera ──────────────────────────────────────
  'Live Photo Capture': 'Live-Aufnahme',
  'Camera Failed': 'Kamera fehlgeschlagen',
  'Restart Camera': 'Kamera neu starten',
  'Start camera': 'Kamera starten',
  'Enable Camera': 'Kamera aktivieren',
  'Starting camera...': 'Kamera wird gestartet …',
  'Position Your Face First': 'Bring zuerst dein Gesicht ins Bild',
  'Position your face in the center of the frame': 'Bring dein Gesicht in die Bildmitte',
  'Center your face in the frame and hold still': 'Gesicht mittig halten und still bleiben',
  'Ensure good lighting -- avoid backlighting': 'Sorge für gutes Licht – kein Gegenlicht',
  'Improve Lighting & Hold Steady': 'Mehr Licht und ruhig halten',
  'We need a live photo to verify your identity':
    'Für die Identitätsprüfung brauchen wir eine Live-Aufnahme',
  'We need your camera to capture a live photo for identity verification.':
    'Wir brauchen deine Kamera für eine Live-Aufnahme zur Identitätsprüfung.',
  'Look directly at the camera and blink twice...':
    'Schau direkt in die Kamera und blinzle zweimal …',
  'Processing your verification...': 'Deine Überprüfung wird verarbeitet …',
  'Live capture cancelled': 'Live-Aufnahme abgebrochen',
  'Liveness verification failed': 'Die Lebendigkeitsprüfung ist fehlgeschlagen',
  'Liveness check': 'Lebendigkeitsprüfung',
  'Liveness check passed': 'Lebendigkeitsprüfung bestanden',
  'Liveness check failed': 'Lebendigkeitsprüfung fehlgeschlagen',
  'Start Liveness Check': 'Lebendigkeitsprüfung starten',
  'Look ahead': 'Geradeaus schauen',
  'Turn slightly': 'Leicht drehen',
  'Selfie upload failed': 'Die Aufnahme konnte nicht gesendet werden',
  'Missing required capture data.': 'Erforderliche Aufnahmedaten fehlen.',

  // ── Handy-Ansicht: Stimme ──────────────────────────────────────
  'Speak these digits': 'Sprich diese Ziffern',
  'Speak the digits shown below into your microphone.':
    'Sprich die unten angezeigten Ziffern in dein Mikrofon.',
  'Start Recording': 'Aufnahme starten',
  'Stop Recording': 'Aufnahme beenden',
  'Submit Voice': 'Aufnahme senden',
  'Get Challenge': 'Aufgabe laden',
  'Request New Challenge': 'Neue Aufgabe anfordern',

  // ── Handy-Ansicht: Fehler ──────────────────────────────────────
  'Unable to Load': 'Laden nicht möglich',
  'Invalid or unrecognised link.': 'Ungültiger oder unbekannter Link.',
  'This link has already been used.': 'Dieser Link wurde bereits verwendet.',
  'This QR code has expired. Please generate a new one on your desktop.':
    'Dieser QR-Code ist abgelaufen. Bitte erzeuge am Rechner einen neuen.',
  'Session response is incomplete. Please try scanning the QR code again.':
    'Die Sitzungsdaten sind unvollständig. Bitte scanne den QR-Code erneut.',
  'OCR timed out. Please try again.':
    'Das Auslesen hat zu lange gedauert. Bitte versuche es erneut.',
  'Validation timed out. Please try again.':
    'Die Prüfung hat zu lange gedauert. Bitte versuche es erneut.',
  'Verification is taking too long. Please close and try again.':
    'Die Überprüfung dauert zu lange. Bitte schließe das Fenster und versuche es erneut.',
  'The photo of your ID is not clear enough. Please retake it in good lighting.':
    'Das Bild deines Ausweises ist nicht scharf genug. Bitte lies ihn bei gutem Licht erneut ein.',
  'We were unable to verify your identity. Please return to your desktop to see details.':
    'Deine Identität konnte nicht bestätigt werden. Die Details siehst du am Rechner.',
  'Document Verification Failed': 'Dokumentenprüfung fehlgeschlagen',
  'Document verified': 'Dokument bestätigt',
  'Age verified': 'Alter bestätigt',
  'Failed to start': 'Start fehlgeschlagen',
  'Failed to restart': 'Neustart fehlgeschlagen',
  'Camera access failed:': 'Kamerazugriff fehlgeschlagen:',
  'Try again': 'Erneut versuchen',
  Skip: 'Überspringen',

  // ── Abschluss auf dem Handy ────────────────────────────────────
  "You're all set": 'Alles erledigt',
  'Your identity has been verified. You can close this tab and return to your desktop.':
    'Deine Identität wurde bestätigt. Du kannst diesen Tab schließen und zum Rechner zurückkehren.',
  'Your document has been verified. You can close this tab and return to your desktop.':
    'Dein Dokument wurde bestätigt. Du kannst diesen Tab schließen und zum Rechner zurückkehren.',
  'Your age has been verified. You can close this tab and return to your desktop.':
    'Dein Alter wurde bestätigt. Du kannst diesen Tab schließen und zum Rechner zurückkehren.',
  'Finalizing your verification': 'Überprüfung wird abgeschlossen',
  'Analyzing your live photo': 'Deine Live-Aufnahme wird ausgewertet',
  'Document read': 'Dokument gelesen',
  'Details matched': 'Daten abgeglichen',
  'Security checks': 'Sicherheitsprüfungen',

  // ── Weitere Statustexte ────────────────────────────────────────
  'Uploading...': 'Wird hochgeladen …',
  'Starting Camera...': 'Kamera wird gestartet …',
  'Camera Access Required': 'Kamerazugriff erforderlich',
  CAMERA: 'KAMERA',
  CAPTURED: 'AUFGENOMMEN',
  'Barcode / QR scanning': 'Barcode- und QR-Prüfung',
  'Get Challenge Digits': 'Ziffern anfordern',
  'Submit Voice Capture': 'Sprachaufnahme senden',
  'Your verification is under manual review. You will be notified of the result.':
    'Deine Überprüfung wird manuell geprüft. Du wirst über das Ergebnis informiert.',
  Passed: 'Bestanden',
  Failed: 'Fehlgeschlagen',
  Smile: 'Lächeln',

  // ── Kopfzeile der Einstiegsseite ───────────────────────────────
  // Wird mit dem Firmennamen zusammengesetzt: "Identitätsprüfung für <Firma>"
  'Verify with': 'Identitätsprüfung für',
  "Choose how you'd like to complete verification":
    'Wähle, wie du dich ausweisen möchtest',
  'Upload your ID to confirm you are': 'Lade deinen Ausweis hoch als Nachweis, dass du',

  // ── QR-Übergabe aufs Handy ─────────────────────────────────────
  'Note:': 'Hinweis:',
  'This link points to localhost and cannot be opened by a phone. A reachable address is required for the QR code to work.':
    'Dieser Link zeigt auf localhost und kann vom Handy nicht geöffnet werden. Für den QR-Code wird eine erreichbare Adresse benötigt.',
  'Waiting for phone…': 'Warte auf das Handy …',
  'Cancel — use this device instead': 'Abbrechen – dieses Gerät verwenden',

  // ── Handy-Ablauf: Schrittzeile ─────────────────────────────────
  // Wird zusammengesetzt: "Schritt 1 von 3 — Vorderseite"
  Step: 'Schritt',
  of: 'von',
  'Front of ID': 'Vorderseite',
  'Back of ID': 'Rückseite',
  Verification: 'Prüfung',
  'Upload ID': 'Ausweis hochladen',

  // ── Handy-Ablauf: Überschriften und Anleitungen ────────────────
  'Scan the front of your ID': 'Vorderseite einlesen',
  'Scan the back of your ID': 'Rückseite einlesen',
  'Upload your ID to verify your age': 'Ausweis hochladen zum Altersnachweis',
  'Position your ID card and take a clear photo. Make sure all four corners are visible and the text is clear.':
    'Leg deinen Ausweis flach hin und achte auf ein scharfes, gut lesbares Bild. Alle vier Ecken müssen sichtbar und die Schrift lesbar sein.',
  'Turn your ID over and photograph the back. The barcode or machine-readable zone must be fully visible.':
    'Dreh den Ausweis um und lies die Rückseite ein. Barcode oder maschinenlesbare Zone müssen vollständig sichtbar sein.',

  // ── Handy-Ablauf: Tipp-Zeilen ──────────────────────────────────
  'Good lighting · No glare · Hold steady': 'Gutes Licht · Keine Spiegelung · Ruhig halten',
  'Barcode must be unobstructed': 'Barcode darf nicht verdeckt sein',
  'Remove glasses · Face well-lit · No hat': 'Brille abnehmen · Gesicht gut beleuchtet · Keine Kopfbedeckung',

  // ── Handy-Ablauf: Statusanzeigen im Sucher ─────────────────────
  'READING FRONT': 'VORDERSEITE WIRD GELESEN',
  'READING BARCODE': 'BARCODE WIRD GELESEN',

  // ── Kamera-Komponenten ─────────────────────────────────────────
  'Go Back': 'Zurück',
  'Document captured!': 'Ausweis eingelesen',
  'Photo captured!': 'Aufnahme gemacht',
  'Move closer to your ID': 'Näher an den Ausweis',
  'Position the front of your ID': 'Vorderseite ins Bild bringen',
  'Position the barcode side': 'Rückseite mit Barcode ins Bild bringen',
  'Unable to access camera. Please check permissions.':
    'Kein Zugriff auf die Kamera. Bitte prüfe die Berechtigungen.',
  'Unable to access front camera. Please check permissions.':
    'Kein Zugriff auf die Frontkamera. Bitte prüfe die Berechtigungen.',
  'Could not restart camera.': 'Die Kamera konnte nicht neu gestartet werden.',

  // ── Rückseiten-Upload ──────────────────────────────────────────
  'Enhanced Verification Features:': 'Zusätzliche Prüfungen:',
  'Scanning QR codes and barcodes': 'QR-Codes und Barcodes werden gelesen',
  'Cross-validating with front-of-ID': 'Abgleich mit der Vorderseite',
  'Verifying authenticity': 'Echtheitsprüfung',
  'Please wait while we process your document': 'Dein Dokument wird verarbeitet',
  'Why upload the back-of-ID?': 'Warum die Rückseite?',
  'Cross-Validation:': 'Datenabgleich:',
  'Higher Accuracy:': 'Höhere Genauigkeit:',
  'Security Features:': 'Sicherheitsmerkmale:',
  'Upload the back of your ID for QR/barcode scanning and cross-validation':
    'Lade die Rückseite hoch – für Barcode-Prüfung und Abgleich mit der Vorderseite',
  'Please upload a valid image file (JPEG, PNG, or WebP)':
    'Bitte lade eine gültige Bilddatei hoch (JPEG, PNG oder WebP)',
  'File size must be less than 10MB': 'Die Datei darf höchstens 10 MB groß sein',
  'Cross-validation failed': 'Der Datenabgleich ist fehlgeschlagen',
  'We check your date of birth to confirm you are':
    'Wir prüfen dein Geburtsdatum als Nachweis, dass du mindestens',
  'No other data is stored.': 'Weitere Daten werden nicht gespeichert.',

  // ── Kamera-Führung (Live-Hinweise im Sucher) ───────────────────
  'Hold steady…': 'Ruhig halten …',
  'Perfect! Capturing…': 'Perfekt – Aufnahme läuft …',
  'Center your face… hold steady': 'Gesicht mittig – ruhig halten',
  'Starting camera…': 'Kamera wird gestartet …',

  // ── Einwilligung Gesichtsabgleich (DSGVO Art. 9) ───────────────
  // Rechtlich verbindlicher Text. Änderungen hier sind KEINE reinen
  // Übersetzungsänderungen — bei inhaltlicher Abweichung muss
  // CONSENT_TEXT_VERSION in BiometricConsent.tsx hochgezählt werden,
  // sonst lässt sich nicht mehr belegen, worin jemand eingewilligt hat.
  'Consent for the face match': 'Einverständnis für den Gesichtsabgleich',
  'For this check we compare your face with the photo in your document. We need your explicit consent for that.':
    'Für diese Prüfung vergleichen wir dein Gesicht mit dem Foto in deinem Ausweis. Dafür brauchen wir dein ausdrückliches Einverständnis.',
  'What happens': 'Was passiert',
  'You read in your document and then record a short video in which you turn your head. A computer compares both faces and checks that a real person is in front of the camera.':
    'Du liest deinen Ausweis ein und machst anschließend eine Videoaufnahme, bei der du den Kopf drehst. Ein Computer vergleicht beide Gesichter und prüft, ob eine echte Person vor der Kamera ist.',
  Why: 'Warum',
  'So we can be sure that you are the person shown in the document.':
    'Damit wir sicher sein können, dass du die Person bist, die im Ausweis steht.',
  'How long we keep it': 'Wie lange wir das speichern',
  'The recordings are deleted after at most {days} days. We keep the result of the check for longer, without the images.':
    'Die Aufnahmen werden spätestens nach {days} Tagen gelöscht. Das Ergebnis der Prüfung – bestanden oder nicht – bewahren wir länger auf, ohne die Bilder.',
  Voluntary: 'Freiwillig',
  'You do not have to agree. Without the face match we only check your document — that continues right here and takes even less time. You will not suffer any disadvantage.':
    'Du musst dem nicht zustimmen. Ohne Gesichtsabgleich prüfen wir nur deinen Ausweis – das geht direkt hier weiter und dauert sogar kürzer. Dir entstehen dadurch keine Nachteile.',
  Withdrawal: 'Widerruf',
  'You can withdraw your consent at any time — informally by email to':
    'Du kannst dein Einverständnis jederzeit zurückziehen – formlos per E-Mail an',
  'You can withdraw your consent at any time — informally at your driving school.':
    'Du kannst dein Einverständnis jederzeit zurückziehen – formlos in deiner Fahrschule.',
  'We will delete the recordings immediately. Checks already completed are not affected.':
    'Wir löschen die Aufnahmen dann umgehend. Am Ergebnis einer bereits abgeschlossenen Prüfung ändert das nichts.',
  'I agree': 'Ich bin einverstanden',
  'Continue without face match': 'Ohne Gesichtsabgleich fortfahren',
  'Could not continue without the face match. Please try again.':
    'Es konnte nicht ohne Gesichtsabgleich fortgefahren werden. Bitte versuche es erneut.',
};
